import { motion } from 'framer-motion';
import { Users, Layers, Sliders, Cpu } from 'lucide-react';

interface CapabilityCardProps {
  title: string;
  description: string;
  subDescription?: string;
  icon: React.ReactNode;
  variant: 'orange' | 'black';
  index: number;
}

const CapabilityCard: React.FC<CapabilityCardProps> = ({
  title,
  description,
  subDescription,
  icon,
  variant,
  index,
}) => {
  const isOrange = variant === 'orange';

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`
        relative rounded-3xl p-6 sm:p-8 overflow-hidden
        ${isOrange ? 'bg-orange-500' : 'bg-gray-900'}
      `}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        {/* Content */}
        <div className="flex-1">
          <h3
            className={`
              text-2xl sm:text-3xl lg:text-4xl font-bold mb-3
              ${isOrange ? 'text-white' : 'text-white'}
            `}
          >
            {title}
          </h3>
          <p
            className={`
              text-sm sm:text-base mb-2
              ${isOrange ? 'text-white/90' : 'text-gray-300'}
            `}
          >
            {description}
          </p>
          {subDescription && (
            <p
              className={`
                text-xs sm:text-sm
                ${isOrange ? 'text-white/70' : 'text-gray-400'}
              `}
            >
              {subDescription}
            </p>
          )}
        </div>

        {/* Icon */}
        <motion.div
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ duration: 0.3 }}
          className={`
            w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center flex-shrink-0
            ${isOrange ? 'bg-white/20' : 'bg-orange-500/20'}
          `}
        >
          <div className={isOrange ? 'text-white' : 'text-orange-500'}>{icon}</div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const CoreCapabilities: React.FC = () => {
  const capabilities = [
    {
      title: 'Characters no longer drift.',
      description: 'Across shots, scenes, and timelines, identities remain stable.',
      subDescription: 'Supports multiple characters in a single frame with clear distinction and consistency.',
      icon: <Users size={32} />,
      variant: 'orange' as const,
    },
    {
      title: 'Multiple characters in the same frame',
      description: 'Stable appearance、Clear spatial relationships、Logical behavior continuity；',
      subDescription: 'No more character swapping, merging, or distortion.',
      icon: <Layers size={32} />,
      variant: 'black' as const,
    },
    {
      title: 'Fine-tune individual shots with precision',
      description: 'Character details、Composition、Motion logic、Emotional tone',
      subDescription: 'Not regeneration. Correction.',
      icon: <Sliders size={32} />,
      variant: 'orange' as const,
    },
    {
      title: 'Built on advanced model architecture:',
      description: 'Integrated with Google Nano Banana Pro、Powered by Seedance 2.0',
      subDescription: 'A proprietary control layer enables higher stability and creative precision.',
      icon: <Cpu size={32} />,
      variant: 'black' as const,
    },
  ];

  return (
    <section className="relative bg-white py-20 lg:py-32 overflow-hidden">
      <div className="px-4 sm:px-6 lg:px-8 xl:px-12 max-w-5xl mx-auto">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-orange-500">Core Capabilities</h2>
        </motion.div>

        {/* Capability Cards */}
        <div className="space-y-4 sm:space-y-6">
          {capabilities.map((capability, index) => (
            <CapabilityCard
              key={capability.title}
              title={capability.title}
              description={capability.description}
              subDescription={capability.subDescription}
              icon={capability.icon}
              variant={capability.variant}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreCapabilities;
